import styled from "@emotion/styled";
import type { ChangeEvent, FocusEvent, KeyboardEvent, MouseEvent } from "react";
import { useCallback, useMemo, useState } from "react";

import { shade, tint } from "@skilitics/designer-theme";
import {
  Button,
  ConfirmationDialog,
  DEFAULT_INPUT_HEIGHT,
  destructiveActionFormatStrings,
  IconButton,
  OrderableList,
  PrefixChip,
  PROP_PANEL_PADDING,
  SkiliticsIcon,
  Text,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useNavigateToResponseId, useSelectedResponseId } from "../../selection/useSelectedAspect";
import { useHandlerDesigner } from "../HandlerDesignerContext";

const ButtonContainer = styled.div`
  position: absolute;
  top: 4px;
  right: ${PROP_PANEL_PADDING}px;

  display: flex;
  align-items: center;

  opacity: 0;
  pointer-events: none;
`;

const DragIconContainer = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  cursor: move;
  color: ${({ theme }) => theme.generalTextIcon};
  opacity: 0;
`;

const SelectableContainer = styled.div<{ $selected?: boolean; $disabled?: boolean }>`
  position: relative;
  display: flex;
  width: auto;
  margin-left: -${PROP_PANEL_PADDING}px;
  margin-right: -${PROP_PANEL_PADDING}px;

  border-left: 4px solid ${({ $selected, theme }) => ($selected ? theme.primary : "transparent")};
  background-color: ${({ $selected = false, theme }) => ($selected ? theme.primaryContainer : theme.surface)};

  ${({ $disabled }) => $disabled && `opacity: 0.4;`};

  overflow: hidden;

  &:hover {
    background-color: ${({ $selected = false, theme }) =>
      $selected ? shade(theme.primaryContainer, 0.05) : theme.primaryContainer};

    ${DragIconContainer} {
      opacity: 1;
    }

    ${ButtonContainer} {
      opacity: 1;
      pointer-events: auto;

      // todo: tidy up the coloring (dark mode primary container uses alpha...)
      ${IconButton} {
        background-color: ${({ $selected = false, theme }) =>
          theme.type === "light"
            ? $selected
              ? shade(theme.primaryContainer, 0.05)
              : theme.primaryContainer
            : $selected
              ? shade(theme.surfaceSoft, 0.05)
              : theme.surfaceVariant};

        transition: background-color 0s;
      }

      ${IconButton}:hover {
        background-color: ${({ $selected = false, theme }) =>
          theme.type === "light"
            ? shade(theme.primaryContainer, $selected ? 0.1 : 0.05)
            : $selected
              ? tint(theme.surfaceSoft, 0.02)
              : tint(theme.surfaceVariant, 0.05)};

        transition: background-color 0s;
      }
    }
  }

  &:has(input:focus) {
    ${ButtonContainer} ${IconButton} {
      opacity: 0;
      pointer-events: none;
    }
  }
`;

const ResponseListContainer = styled.div`
  ${Button} {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 15px;
  }
`;

const Content = styled.div``;

const ContentContainer = styled.div`
  display: grid;
  grid-template-rows: ${DEFAULT_INPUT_HEIGHT + 6}px;
  grid-template-columns: 20px 1fr auto;

  width: 100%;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  width: 100%;
  height: 100%;
`;

const StyledText = styled(Text)`
  width: 100%;
`;

const ResponseLabel = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  overflow: hidden;

  padding-inline-start: 10px;
`;

export const ResponsesDesigner = () => {
  const { getResponses, removeResponse, reorderResponse, updateResponse } = useHandlerDesigner();
  const formatMessage = useFormatMessage(mergedFormatStrings);

  const [editingResponse, setEditingResponse] = useState<{ id: number; label: string } | undefined>();
  const [confirmDialogResponseId, setConfirmDialogResponseId] = useState<number | null>(null);

  const navigateToResponseId = useNavigateToResponseId();
  const selectedResponseId = useSelectedResponseId();

  const responses = useMemo(() => {
    const initialResponses = getResponses();
    let isCatchAllIncluded = false;
    const responseList: typeof initialResponses = [];
    // If there is a catch all, then mark all subsequent responses as "disabled", as they will not be evaluated.
    for (const response of initialResponses) {
      responseList.push({
        ...response,
        disabled: isCatchAllIncluded ?? undefined,
      });
      if (response.catchAll) {
        isCatchAllIncluded = true;
      }
    }
    return responseList;
  }, [getResponses]);

  const handleOrderChange = useCallback(
    (key: string | number, newIndex: number) => reorderResponse(Number(key), newIndex),
    [reorderResponse],
  );

  const handleResponseClick = useCallback(
    (id: number) => (e: MouseEvent) => {
      e.stopPropagation();
      if (!editingResponse) {
        navigateToResponseId(id);
      }
    },
    [navigateToResponseId, editingResponse],
  );

  const removeResponseById = useCallback(
    (id: number) => {
      removeResponse(id);
      if (selectedResponseId === id) {
        navigateToResponseId(undefined);
      }
    },
    [removeResponse, navigateToResponseId, selectedResponseId],
  );

  const openResponseLabelEdit = useCallback(
    (id: number) => {
      const label = responses.find((r) => r.id === id)?.label || "";
      setEditingResponse({ id, label });
    },
    [setEditingResponse, responses],
  );

  const closeResponseLabelEdit = useCallback(() => {
    setEditingResponse(undefined);
  }, [setEditingResponse]);

  const handleResponseLabelUpdate = useCallback(() => {
    if (!editingResponse) return;

    updateResponse(editingResponse.id, { label: editingResponse.label });
    closeResponseLabelEdit();
  }, [updateResponse, editingResponse, closeResponseLabelEdit]);

  const handleResponseLabelBlur = useCallback(
    (e: FocusEvent) => {
      e.preventDefault();
      // Prevent the blur if specifically clicking on the edit button again.
      if (editingResponse && e.relatedTarget?.id !== `responseEdit${editingResponse.id}`) {
        handleResponseLabelUpdate();
      }
    },
    [handleResponseLabelUpdate, editingResponse],
  );

  const handleResponseKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        handleResponseLabelUpdate();
      }
    },
    [handleResponseLabelUpdate],
  );

  const createHandleEditResponseClick = (id: number) => (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingResponse) {
      handleResponseLabelUpdate();
      if (editingResponse.id === id) {
        return; // Clicking edit button again will close input, clicking edit on another response will start editing another response.
      }
    }
    openResponseLabelEdit(id);
  };

  const handleResponseLabelChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (editingResponse) {
      setEditingResponse((response) => ({
        ...(response ?? editingResponse),
        label: e.target?.value || "",
      }));
    }
  };

  // const activeResponse = useMemo(() => responses.find((r) => r.id === responseId), [responses, responseId]);

  // useEffect(() => {
  //   // If the active response changes (by selecting or adding new), and the label is empty, then focus on the response label.
  //   if (activeResponse && !activeResponse?.label) {
  //     setEditingResponse({ id: activeResponse.id, label: "" });
  //   }
  // }, [activeResponse]);

  return (
    <ResponseListContainer data-test="responses-container">
      <Content data-test="content-container">
        <OrderableList
          handle={false}
          orderByKey={responses.map(({ id }) => `${id}`)}
          onOrderChange={handleOrderChange}
          data-test="response-list"
        >
          {responses.map(({ label, id, catchAll, disabled }, index) => (
            <SelectableContainer
              key={id}
              onClick={handleResponseClick(id)}
              data-id={id}
              $selected={selectedResponseId === id}
              $disabled={disabled}
              data-test="response-item"
            >
              <ContentContainer data-test="content-container">
                <DragIconContainer data-test="drag-handle">
                  <SkiliticsIcon icon="drag-grip" />
                </DragIconContainer>

                <ResponseLabel>
                  <PrefixChip
                    type="primary"
                    label={formatMessage(index === 0 ? "first" : catchAll ? "last" : "middle")}
                  />
                  {!!editingResponse && editingResponse.id === id ? (
                    <StyledText
                      onChange={handleResponseLabelChange}
                      value={editingResponse.label}
                      onBlur={handleResponseLabelBlur}
                      autoFocus={true}
                      onKeyDown={handleResponseKeyDown}
                      dataTest="label"
                    />
                  ) : (
                    <Label onDoubleClick={createHandleEditResponseClick(id)} data-test="label">
                      {label || (catchAll && index !== 0 ? "" : formatMessage("untitledResponse"))}
                    </Label>
                  )}
                </ResponseLabel>

                <ButtonContainer data-test="buttons-container">
                  <Tooltip title={formatMessage("deleteResponse")} placement="top">
                    <IconButton onClick={() => setConfirmDialogResponseId(id)} icon="minus" data-test="delete-button" />
                  </Tooltip>
                </ButtonContainer>
              </ContentContainer>
            </SelectableContainer>
          ))}
        </OrderableList>

        <ConfirmationDialog
          header={formatMessage("confirmHeader")}
          message={formatMessage("confirmMessage")}
          open={confirmDialogResponseId !== null}
          confirmLabel={formatMessage("confirmLabel")}
          cancelLabel={formatMessage("cancelLabel")}
          onConfirm={(confirmed) => {
            if (confirmed && confirmDialogResponseId !== null) {
              removeResponseById(confirmDialogResponseId);
            }
            setConfirmDialogResponseId(null);
          }}
        />
      </Content>
    </ResponseListContainer>
  );
};

const formatStrings = {
  first: {
    en: "If",
  },
  middle: {
    en: "Else If",
  },
  last: {
    en: "Else",
  },
  confirmHeader: {
    en: "Delete Response",
  },
  confirmMessage: {
    en: `Are you sure you want to delete this response? This cannot be undone.`,
  },
  untitledResponse: {
    en: "Untitled Response",
  },
  deleteResponse: {
    en: "Delete Response",
  },
} satisfies FormatMessages;

const mergedFormatStrings = { ...formatStrings, ...destructiveActionFormatStrings };
